<template>
  <ServiceListPageLayout
    v-model:keyword="searchKeyword"
    :title="isProductSimulatorMode ? '产品模拟器中心' : '帮助中心'"
    :filter-active="hasActiveFilter"
    :empty-state="isEmptyState"
    page-class="help-center-page"
    enable-search-suggestions
    :search-suggestions="searchSuggestions"
    :search-suggestions-loading="searchSuggestionsLoading"
    :search-suggestions-error="searchSuggestionsError"
    @search="handleSearch"
    @search-focus="handleSearchFocus"
    @filter="openMobileMenu"
    @suggestion-select="handleSuggestionSelect"
    @suggestion-retry="retrySearchSuggestions"
  >
    <template #sidebar>
      <div class="help-sidebar__menu-wrapper">
        <div class="help-sidebar__menu-inner">
          <Menu
            :categories="['product', 'scene', 'help']"
            :selected-key="selectedCategoryId"
            :accordion="true"
            :facet-counts="facetCounts"
            default-expand="first"
            @select="handleCategorySelect"
          />
        </div>
      </div>
    </template>

    <div v-if="listError" class="help-content__error" role="alert">
      <p>帮助内容加载失败，请稍后重试</p>
      <a-button @click="fetchContentList()">重试</a-button>
    </div>
    <ContentList
      v-else
      :content-list="contentList"
      :total="total"
      :current="currentPage"
      :page-size="pageSize"
      :loading="loading"
      :keyword="submittedSearchKeyword"
      @page-change="handlePageChange"
      @item-click="handleContentClick"
    />

    <template #after>
      <!-- 移动端筛选分类（底部弹窗） -->
      <MobilePopup
        v-model:visible="mobileMenuVisible"
        title="筛选"
        height="calc(100dvh - 58px)"
        panel-class="help-filter-drawer"
        @close="closeMobileMenu"
      >
        <div class="help-popup-content">
          <Menu
            :categories="['product', 'scene', 'help']"
            :selected-key="selectedCategoryId"
            :accordion="true"
            :facet-counts="facetCounts"
            default-expand="first"
            @select="handleMobileCategorySelect"
          />
        </div>
      </MobilePopup>
    </template>
  </ServiceListPageLayout>
</template>

<script setup>
/**
 * @description 帮助中心页面
 */

import { ref, onMounted, computed, watch } from 'vue'
import { BaseMessage } from '~/utils/baseMessage'
import { websiteApi } from '@/api/website'
import { baseApi } from '@/api/base'
import { getOssUrl } from '@/utils'
import { isOssSignedUrlExpiring } from '@/utils/ossSignedUrl'
import { openResolvedPreview } from '@/utils/contentPreviewAction'
import {
  canDownloadFile,
  downloadFileByUrl,
  getFileOpenUrl,
  resolveDownloadFileName,
} from '@/utils/download'
import ServiceListPageLayout from '~/components/website/service/ServiceListPageLayout.vue'
import Menu from '~/components/website/service/help/Menu.vue'
import ContentList from '~/components/website/service/help/ContentList.vue'
import MobilePopup from '~/components/mobile/Popup.vue'
import { useServiceContentSuggestions } from '~/composables/useServiceContentSuggestions'
import { useRoute, useRouter } from 'vue-router'
import { CONCURRENCY_KEYS, isCancelForConcurrency } from '~/api/concurrency'
import { normalizeServiceSearchKeyword } from '~/utils/website/serviceContentSearch'
import { createPublicContentSsrRequestConfig } from '~/utils/website/ssrRequestPolicy'
import { usePageTracking, trackClickEvent } from '~/composables/usePageTracking'
import { PAGE_TRACKING } from '~/utils/tracking/pageRegistry'

definePageMeta({
  layout: 'website',
})

usePageTracking(PAGE_TRACKING.helpCenter)

const { setSeo } = useSeo()
setSeo({
  title: '帮助中心',
  description: 'Tenda 帮助中心，为您提供产品使用指南、售后服务、购买帮助等全方位支持。',
})

const route = useRoute()
const router = useRouter()
const { openExternal } = useBasePath()
/**
 * @description 桌面端左侧菜单：与下载中心相同 DOM/CSS（aside 上 sticky、内部 max-height 滚动、主栏白底卡片）。
 */

const contentList = ref([])
const selectedCategoryId = ref('all') // 默认「全部分类」，不筛选
const searchKeyword = ref('')
const submittedSearchKeyword = ref('')
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)
const loading = ref(true)
const listError = ref(false)
const contentListRequestId = ref(0)
const mobileMenuVisible = ref(false)
const searchSuggestionsReady = ref(false)
const skipNextSuggestionSchedule = ref(false)
const isProductSimulatorMode = ref(false)
const hasActiveFilter = computed(
  () => selectedCategoryId.value !== 'all' || isProductSimulatorMode.value
)
const {
  suggestions: searchSuggestions,
  suggestionsLoading: searchSuggestionsLoading,
  suggestionsError: searchSuggestionsError,
  scheduleSuggestions: scheduleSearchSuggestions,
  clearSuggestions: clearSearchSuggestions,
} = useServiceContentSuggestions({ centerType: 2 })

/**
 * @description 查询参数
 * @type {Object}
 */
const queryParams = ref({
  prodIds: undefined,
  secondSceneIds: undefined,
  thirdContentCategoryIds: undefined,
})

const getContentFilters = () => ({
  prodIds: [...(queryParams.value.prodIds || [])],
  secondSceneIds: [...(queryParams.value.secondSceneIds || [])],
  thirdContentCategoryIds: [...(queryParams.value.thirdContentCategoryIds || [])],
})
const facetCounts = ref(null)
const fetchHelpFacets = async () => {
  try {
    // 选项按帮助中心内自身是否有内容一次确定，不随分类或搜索条件变化。
    const response = await websiteApi.getContentFacets({
      centerType: 2,
    })
    if (!response?.product || !response?.scene || !response?.category || !response?.stage) {
      throw new Error('帮助筛选计数响应格式异常')
    }
    facetCounts.value = response
  } catch (error) {
    facetCounts.value = null
    console.error('获取帮助筛选计数失败:', error)
  }
}

/**
 * @description Normalize a route query value into a single string.
 * @param {string|string[]|undefined} value - Route query value.
 * @returns {string}
 */
const getSingleQueryValue = (value) => {
  if (Array.isArray(value)) {
    return typeof value[0] === 'string' ? value[0] : ''
  }
  return typeof value === 'string' ? value : ''
}

/**
 * @description 从路径key中解析出分类类型和ID
 * @param {string} key - 路径key，格式：parent1-parent2-itemId
 * @returns {Object|null} - { type: 'product'|'scene'|'help', id: number } 或 null
 */
const parseCategoryKey = (key) => {
  if (!key) return null

  const parts = String(key).split('-')
  if (parts.length < 2) return null

  const firstPart = parts[0]

  // 判断分类类型
  let type = null
  if (firstPart === 'product') {
    type = 'product'
  } else if (firstPart === 'application') {
    type = 'scene'
  } else if (firstPart === 'question') {
    type = 'help'
  } else {
    return null
  }

  // 提取最后一个ID（最终分类ID）
  const idStr = parts[parts.length - 1]
  const id = Number(idStr)
  if (isNaN(id)) return null

  return { type, id }
}

/**
 * @description Sync menu selection and backend filter params by category key.
 * @param {string} key - Selected category key.
 */
const applyCategorySelection = (key = 'all') => {
  selectedCategoryId.value = key || 'all'

  const categoryInfo = parseCategoryKey(selectedCategoryId.value)

  if (categoryInfo?.type === 'product') {
    queryParams.value.prodIds = [categoryInfo.id]
    queryParams.value.secondSceneIds = undefined
    queryParams.value.thirdContentCategoryIds = undefined
    return
  }

  if (categoryInfo?.type === 'scene') {
    queryParams.value.secondSceneIds = [categoryInfo.id]
    queryParams.value.prodIds = undefined
    queryParams.value.thirdContentCategoryIds = undefined
    return
  }

  if (categoryInfo?.type === 'help') {
    queryParams.value.thirdContentCategoryIds = [categoryInfo.id]
    queryParams.value.prodIds = undefined
    queryParams.value.secondSceneIds = undefined
    return
  }

  queryParams.value.prodIds = undefined
  queryParams.value.secondSceneIds = undefined
  queryParams.value.thirdContentCategoryIds = undefined
}

/**
 * @description Build the persisted help-list query for detail navigation and list restore.
 * @returns {Record<string, string>}
 */
const buildHelpListQuery = () => {
  const query = {}

  if (currentPage.value > 1) query.page = String(currentPage.value)
  if (isProductSimulatorMode.value) query.contentType = 'product-simulator'
  const keyword = normalizeServiceSearchKeyword(submittedSearchKeyword.value)
  if (keyword) query.keyword = keyword
  if (selectedCategoryId.value && selectedCategoryId.value !== 'all') {
    query.category = selectedCategoryId.value
  }

  return query
}

const persistedListQuery = computed(() => buildHelpListQuery())
const isEmptyState = computed(
  () => !loading.value && !listError.value && contentList.value.length === 0
)

/**
 * @description Restore list state from URL query.
 */
const syncQueryToState = () => {
  const pageValue = Number.parseInt(getSingleQueryValue(route.query.page), 10)
  currentPage.value = Number.isNaN(pageValue) || pageValue < 1 ? 1 : pageValue
  const keyword = normalizeServiceSearchKeyword(getSingleQueryValue(route.query.keyword))
  searchKeyword.value = keyword
  submittedSearchKeyword.value = keyword
  isProductSimulatorMode.value =
    getSingleQueryValue(route.query.contentType) === 'product-simulator'
  applyCategorySelection(getSingleQueryValue(route.query.category) || 'all')
}

/**
 * @description Sync the current list state back to the URL so detail return keeps context.
 */
const syncStateToQuery = () => {
  if (!import.meta.client) return
  const query = buildHelpListQuery()
  const resolved = router.resolve({
    path: route.path,
    query: Object.keys(query).length ? query : undefined,
  })
  window.history.replaceState(window.history.state, '', resolved.href)
}

/**
 * @description 获取内容列表
 */
const fetchContentList = async (requestConfig = {}, { preserveExisting = false } = {}) => {
  const requestId = contentListRequestId.value + 1
  contentListRequestId.value = requestId
  loading.value = true
  listError.value = false
  try {
    const params = {
      current: currentPage.value,
      size: pageSize.value,
      centerType: 2,
    }

    if (submittedSearchKeyword.value) {
      params.keyword = submittedSearchKeyword.value
    }

    Object.assign(params, getContentFilters())

    const response = isProductSimulatorMode.value
      ? await websiteApi.getProductSimulatorList(
          {
            page: currentPage.value,
            pageSize: pageSize.value,
          },
          requestConfig
        )
      : await websiteApi.getContentList(params, {
          concurrencyKey: CONCURRENCY_KEYS.HELP_CONTENT_LIST,
          ...requestConfig,
        })
    if (requestId !== contentListRequestId.value) return

    const data = response?.data || response || {}

    const records = Array.isArray(data.records) ? data.records : []
    const totalCount = Number(data.total) || 0

    contentList.value = records
    total.value = totalCount
    loading.value = false
  } catch (error) {
    if (
      isCancelForConcurrency(error, CONCURRENCY_KEYS.HELP_CONTENT_LIST) ||
      requestId !== contentListRequestId.value
    ) {
      return
    }
    console.error('获取内容列表失败:', error)
    listError.value = true
    if (!preserveExisting) {
      contentList.value = []
      total.value = 0
    }
    loading.value = false
  }
}

/**
 * @description 处理分类选择
 * @param {string} key - 选中的菜单项key（带路径）
 */
const handleCategorySelect = (key) => {
  currentPage.value = 1
  isProductSimulatorMode.value = false
  applyCategorySelection(key)
  syncStateToQuery()
  fetchContentList()
  if (searchKeyword.value.trim()) {
    scheduleSearchSuggestions(searchKeyword.value, getContentFilters())
  }
}

/**
 * @description 处理移动端分类选择
 */
const handleMobileCategorySelect = (categoryId) => {
  handleCategorySelect(categoryId)
  closeMobileMenu()
}

/**
 * @description 打开移动端菜单
 */
const openMobileMenu = () => {
  mobileMenuVisible.value = true
}

/**
 * @description 关闭移动端菜单
 */
const closeMobileMenu = () => {
  mobileMenuVisible.value = false
}

/**
 * @description 处理搜索
 */
const handleSearch = (keyword) => {
  clearSearchSuggestions()
  if (typeof keyword === 'string') {
    const normalizedKeyword = normalizeServiceSearchKeyword(keyword)
    if (searchKeyword.value !== normalizedKeyword) {
      skipNextSuggestionSchedule.value = true
    }
    searchKeyword.value = normalizedKeyword
  }
  submittedSearchKeyword.value = normalizeServiceSearchKeyword(searchKeyword.value)
  searchKeyword.value = submittedSearchKeyword.value
  currentPage.value = 1

  syncStateToQuery()
  fetchContentList()
}

const handleSearchFocus = (keyword = searchKeyword.value) => {
  const searchText = normalizeServiceSearchKeyword(keyword)
  if (!searchText) return
  scheduleSearchSuggestions(searchText, getContentFilters())
}

const retrySearchSuggestions = () => {
  const keyword = normalizeServiceSearchKeyword(searchKeyword.value)
  if (keyword) scheduleSearchSuggestions(keyword, getContentFilters())
}

const handleSuggestionSelect = (item) => {
  clearSearchSuggestions()
  const contentId = String(item?.contentId || '').trim()
  if (!import.meta.client || !contentId) return

  if (item.contentForm === 'STATIC_SITE' && item.staticSiteUrl) {
    window.open(item.staticSiteUrl, '_blank', 'noopener,noreferrer')
    return
  }

  const baseQuery = { ...persistedListQuery.value }
  const usesDownloadDetailPage = itemUsesDownloadDetailPage(item)
  const resolved = router.resolve({
    path: usesDownloadDetailPage
      ? `/website/service/download/${encodeURIComponent(contentId)}`
      : `/website/service/help/${encodeURIComponent(contentId)}`,
    query: usesDownloadDetailPage
      ? { ...baseQuery, from: 'help' }
      : Object.keys(baseQuery).length > 0
        ? baseQuery
        : undefined,
  })
  openExternal(resolved.href)
}

/**
 * @description 处理页码变化
 */
const handlePageChange = (page) => {
  currentPage.value = page
  syncStateToQuery()
  fetchContentList()
  if (import.meta.client) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

/** 所属中心：1 下载中心 2 帮助中心（接口 records.centerTypes 可为数组） */
const CENTER_TYPE_DOWNLOAD = 1

/**
 * @description 判断列表项是否应使用下载中心详情页展示（含附件预览等）
 * @param {Object} item - 列表项
 * @returns {boolean}
 */
const itemUsesDownloadDetailPage = (item) => {
  const raw = item?.centerTypes
  if (Array.isArray(raw)) {
    return raw.some((v) => Number(v) === CENTER_TYPE_DOWNLOAD)
  }
  return Number(raw) === CENTER_TYPE_DOWNLOAD
}

const normalizeDownloadActionItem = (item, detail = {}) => {
  const data = detail?.data ?? detail ?? {}
  const videoCover = data.videoCover || item?.videoCover || {}
  const fileUrl = data.fileUrl || item?.fileUrl || ''
  return {
    ...item,
    ...data,
    contentId: data.contentId || item?.contentId,
    fileUrl,
    downloadUrl: data.downloadUrl || (fileUrl ? getOssUrl(fileUrl) : item?.downloadUrl || ''),
    fileName: data.fileName || item?.fileName || '',
    fileType: data.fileType || item?.fileType || '',
    coverUrl: data.coverUrl || videoCover.url || item?.coverUrl || '',
    videoCover,
  }
}

const shouldRefreshDownloadActionItem = (item) => {
  const normalized = normalizeDownloadActionItem(item)
  const fileUrl = normalized.downloadUrl || normalized.fileUrl || ''
  const coverUrl = normalized.coverUrl || normalized.videoCover?.previewUrl || ''
  return !fileUrl || isOssSignedUrlExpiring(fileUrl) || isOssSignedUrlExpiring(coverUrl)
}

const resolveDownloadActionItem = async (item) => {
  if (!item?.contentId || !shouldRefreshDownloadActionItem(item)) {
    return normalizeDownloadActionItem(item)
  }
  const detail = await websiteApi.getContentDetail(item.contentId)
  return normalizeDownloadActionItem(item, detail)
}

const getDownloadPreviewUrl = (item) => {
  const url = item?.downloadUrl || (item?.fileUrl ? getOssUrl(item.fileUrl) : '')
  const coverUrl = item?.coverUrl || item?.videoCover?.url || ''
  return getFileOpenUrl({
    url,
    fileName: resolveDownloadFileName(item),
    fileType: item?.fileType,
    poster: coverUrl ? getOssUrl(coverUrl) : '',
  })
}

const handleDownloadCenterItemClick = async (item, detailHref) => {
  try {
    const result = await openResolvedPreview({
      source: item,
      shouldRefresh: shouldRefreshDownloadActionItem(item),
      refreshSource: resolveDownloadActionItem,
      resolvePreviewUrl: getDownloadPreviewUrl,
      openUrl: openExternal,
    })
    const actionItem = result.source

    if (result.opened) {
      baseApi
        .updateContentStat({ contentId: actionItem.contentId, statType: 1 })
        .catch((error) => console.error('preview stat failed:', error))
      return
    }

    if (canDownloadFile(actionItem)) {
      const actionUrl = actionItem.fileUrl || actionItem.downloadUrl
      if (!actionUrl) throw new Error('File URL is missing')
      const result = await downloadFileByUrl({
        url: actionUrl,
        filename: resolveDownloadFileName(actionItem),
        fileType: actionItem.fileType,
        contentForm: actionItem.contentForm,
      })
      BaseMessage.success(result?.strategy === 'native' ? '已开始下载' : '保存成功')
      baseApi
        .updateContentStat({ contentId: actionItem.contentId, statType: 2 })
        .catch((error) => console.error('download stat failed:', error))
      return
    }
  } catch (error) {
    console.error('open help download item failed:', error)
  }

  if (detailHref) openExternal(detailHref)
}

/**
 * @description 处理内容点击：下载资料进入下载详情，帮助内容进入帮助详情，均在新页签打开。
 * @param {Object} item - 内容列表项
 */
const handleContentClick = async (item) => {
  if (!item.contentId) return

  const title = item?.title || item?.fileName || String(item.contentId)
  trackClickEvent({
    pvEvent: 'pv_HelpCenter_click',
    targetType: 'help_center_content',
    targetId: String(item.contentId),
    targetName: title,
    pageName: '帮助中心',
    properties: {
      content_title: title,
      file_name: title,
      content_id: String(item.contentId),
      action_type: 'preview',
    },
  })

  if (item.contentForm === 'STATIC_SITE' && item.staticSiteUrl) {
    if (import.meta.client) {
      window.open(item.staticSiteUrl, '_blank', 'noopener,noreferrer')
    }
    return
  }

  const baseQuery = { ...persistedListQuery.value }
  const usesDownloadDetailPage = itemUsesDownloadDetailPage(item)
  const detailPath = usesDownloadDetailPage
    ? `/website/service/download/${encodeURIComponent(item.contentId)}`
    : `/website/service/help/${encodeURIComponent(item.contentId)}`

  const resolved = router.resolve({
    path: detailPath,
    query: usesDownloadDetailPage
      ? { ...baseQuery, from: 'help' }
      : Object.keys(baseQuery).length > 0
        ? baseQuery
        : undefined,
  })
  if (import.meta.client) {
    if (usesDownloadDetailPage) {
      await handleDownloadCenterItemClick(item, resolved.href)
      return
    }
    openExternal(resolved.href)
  }
}

watch(searchKeyword, (value) => {
  if (!searchSuggestionsReady.value) return
  if (skipNextSuggestionSchedule.value) {
    skipNextSuggestionSchedule.value = false
    return
  }
  const keyword = normalizeServiceSearchKeyword(value)
  if (!keyword) {
    clearSearchSuggestions()
    return
  }
  scheduleSearchSuggestions(keyword, getContentFilters())
})

if (import.meta.server) {
  syncQueryToState()
}

const publicContentSsrEnabled = usePublicContentSsrEnabled()
const { data: initialHelpPage } = await useAsyncData(
  () => `website-help-list:${route.fullPath}`,
  async () => {
    if (import.meta.client) return { records: [], total: 0 }

    await fetchContentList(createPublicContentSsrRequestConfig())
    return {
      records: contentList.value,
      total: total.value,
    }
  },
  usePublicContentAsyncDataOptions({ default: () => ({ records: [], total: 0 }) })
)

if (publicContentSsrEnabled.value && (import.meta.server || useNuxtApp().isHydrating)) {
  contentList.value = Array.isArray(initialHelpPage.value.records)
    ? initialHelpPage.value.records
    : []
  total.value = Number(initialHelpPage.value.total) || 0
  loading.value = false
}

onMounted(async () => {
  syncQueryToState()
  await Promise.all([fetchContentList({}, { preserveExisting: true }), fetchHelpFacets()])
  searchSuggestionsReady.value = true
})
</script>

<style lang="scss" scoped>
.help-content__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 400px;
  border-radius: 12px;
  background: $bg-white;
  text-align: center;
}

.help-sidebar__menu-wrapper {
  width: 100%;
  box-sizing: border-box;

  @include respond-to('lg') {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
}

.help-sidebar__menu-inner {
  @include respond-to('lg') {
    max-height: calc(100dvh - #{$base-header-height} - 48px);
    overflow-y: auto;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 4px;
    transition: background 0.2s;

    &:hover {
      background: rgba(0, 0, 0, 0.12);
    }

    &:active {
      background: rgba(0, 0, 0, 0.2);
    }
  }
}

.help-popup-content {
  width: 100%;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: $border-dashed-color;
    border-radius: 2px;

    &:hover {
      background: $border-hover-color;
    }
  }

  :deep(.arco-menu-item-selected),
  :deep(.arco-menu-item.arco-menu-selected) {
    color: $text-title-color !important;
    background-color: $bg-color;

    &[data-menu-key='all'] {
      background-color: transparent !important;
    }
  }
}

@include respond-to-max('lg') {
  :deep(.service-list-page__main) {
    margin-top: 4px;
  }

  :deep(.service-list-page__content) {
    margin: 0;
    padding: 0;
    border-radius: 12px;
  }
}
</style>

<style lang="scss">
.arco-drawer-container.base-bottom-drawer.help-filter-drawer {
  .arco-drawer-mask {
    background-color: rgba(0, 0, 0, 0.56);
  }

  .arco-drawer {
    border-radius: 8px 8px 0 0;
    overflow: hidden;
  }

  .arco-drawer-body {
    padding: 0;
  }

  .base-bottom-drawer__header {
    height: 58px;
    min-height: 58px;
    padding: 16px;
    box-sizing: border-box;
  }

  .base-bottom-drawer__content {
    padding: 0 16px;
  }

  .header-action .action-btn--close {
    width: 16px;
    height: 16px;
    color: #a4abb5;
    font-size: 16px;
    line-height: 16px;
  }

  .header-action .action-btn--close .arco-icon {
    width: 16px;
    height: 16px;
  }
}
</style>
